"use client";

import { useState, useEffect } from "react";
import {
  FaEdit,
  FaTrash,
  FaUserPlus,
  FaSave,
  FaTimes,
  FaKey,
  FaUsers,
  FaSignOutAlt,
  FaFlask,
  FaCopy,
  FaCheck,
} from "react-icons/fa";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export const DsportSuperAdminDashboard = () => {
  // States
  const [admins, setAdmins] = useState([]);
  const [testers, setTesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showAddTesterModal, setShowAddTesterModal] = useState(false);
  const [showTesterAccessModal, setShowTesterAccessModal] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [currentTester, setCurrentTester] = useState(null);
  const [activeTab, setActiveTab] = useState("admins");
  const [registrationLink, setRegistrationLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  // Navigation hook
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Form states
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    adminType: 2, // Default to regular admin
  });

  // Tester form states
  const [testerFormData, setTesterFormData] = useState({
    email: "",
    password: "",
  });

  // Access rights state
  const [accessRights, setAccessRights] = useState({
    arena: false,
    coach: false,
    refree: false,
    physician: false,
    dietition: false,
    advertisement: false,
    ecommerce: false,
    informatica: false,
    institute: false,
    rss_news: false,
    news_writing: false,
    news_publishing: false,
  });

  // Tester access rights state
  const [testerAccessRights, setTesterAccessRights] = useState({
    all: false,
    arena: false,
    news: false,
    player_profiling: false,
    informatica: false,
    institute: false,
  });

  // Module names for display
  const moduleDisplayNames = {
    arena: "Arena Management",
    coach: "Coach Management",
    refree: "Referee Management",
    physician: "Physician Management",
    dietition: "Dietitian Management",
    advertisement: "Advertisement Controls",
    ecommerce: "E-Commerce",
    informatica: "Informatica",
    institute: "Institute Management",
    rss_news: "RSS News",
    news_writing: "News Writing",
    news_publishing: "News Publishing",
  };

  // Tester module names for display
  const testerModuleDisplayNames = {
    all: "All Modules",
    arena: "Arena Module",
    news: "News Module",
    player_profiling: "Player Profiling",
    informatica: "Informatica",
    institute: "Institutes",
  };

  // Fetch all admins and testers on component mount
  useEffect(() => {
    fetchAdmins();
    fetchTesters();
  }, []);

  // Fetch admins from API
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/admins`,
        {
          withCredentials: true,
        }
      );
      setAdmins(response.data.admins);
      setLoading(false);
    } catch (err) {
      setError(
        "Failed to fetch admins. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Fetch testers from API
  const fetchTesters = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/testers`,
        {
          withCredentials: true,
        }
      );
      setTesters(response.data.testers);
    } catch (err) {
      setError(
        "Failed to fetch testers. " +
          (err.response?.data?.message || err.message)
      );
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle tester form input changes
  const handleTesterInputChange = (e) => {
    const { name, value } = e.target;
    setTesterFormData({
      ...testerFormData,
      [name]: value,
    });
  };

  // Handle access toggle
  const handleAccessToggle = (module) => {
    setAccessRights({
      ...accessRights,
      [module]: !accessRights[module],
    });
  };

  // Handle tester access toggle
  const handleTesterAccessToggle = (module) => {
    setTesterAccessRights({
      ...testerAccessRights,
      [module]: !testerAccessRights[module],
    });
  };

  // Copy registration link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(registrationLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Open add admin modal
  const openAddModal = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      adminType: 2,
    });
    setShowAddModal(true);
  };

  // Open add tester modal
  const openAddTesterModal = () => {
    setTesterFormData({
      email: "",
      password: "",
    });
    setRegistrationLink("");
    setLinkCopied(false);
    setShowAddTesterModal(true);
  };

  // Open edit admin modal
  const openEditModal = (admin) => {
    setCurrentAdmin(admin);
    setFormData({
      username: admin.username,
      email: admin.email,
      password: "", // Don't prefill password
    });
    setShowEditModal(true);
  };

  // Open access rights modal
  const openAccessModal = async (admin) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/admins/${admin.id}`,
        {
          withCredentials: true,
        }
      );

      setCurrentAdmin(response.data.admin);

      // If no access rights exist, initialize with default structure (all false)
      const defaultAccessRights = {
        arena: false,
        coach: false,
        refree: false,
        physician: false,
        dietition: false,
        advertisement: false,
        ecommerce: false,
        informatica: false,
        institute: false,
        rss_news: false,
        news_writing: false,
        news_publishing: false,
      };

      // Merge existing access rights with default structure
      const adminAccessRights = {
        ...defaultAccessRights,
        ...response.data.admin.accessRights,
      };

      setAccessRights(adminAccessRights);
      setShowAccessModal(true);
      setLoading(false);
    } catch (err) {
      setError(
        "Failed to fetch admin details. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Open tester access rights modal
  const openTesterAccessModal = async (tester) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/testers/${tester.id}`,
        {
          withCredentials: true,
        }
      );

      setCurrentTester(response.data.tester);

      // If no access rights exist, initialize with default structure (all false)
      const defaultTesterAccessRights = {
        all: false,
        arena: false,
        news: false,
        player_profiling: false,
        informatica: false,
        institute: false,
      };

      // Merge existing access rights with default structure
      const testerAccessRights = {
        ...defaultTesterAccessRights,
        ...response.data.tester.accessRights,
      };

      setTesterAccessRights(testerAccessRights);
      setShowTesterAccessModal(true);
      setLoading(false);
    } catch (err) {
      setError(
        "Failed to fetch tester details. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Add new admin
  const addAdmin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/admins`,
        formData,
        { withCredentials: true }
      );
      setShowAddModal(false);
      fetchAdmins();
    } catch (err) {
      setError(
        "Failed to add admin. " + (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Add new tester
  const addTester = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/testers`,
        testerFormData,
        { withCredentials: true }
      );

      setRegistrationLink(response.data.registrationLink);
      fetchTesters();
      setLoading(false);
    } catch (err) {
      setError(
        "Failed to create tester. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Update admin
  const updateAdmin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Only send fields that have values
      const updateData = {};
      if (formData.username) updateData.username = formData.username;
      if (formData.email) updateData.email = formData.email;
      if (formData.password) updateData.password = formData.password;

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/admins/${currentAdmin.id}`,
        updateData,
        {
          withCredentials: true,
        }
      );
      setShowEditModal(false);
      fetchAdmins();
    } catch (err) {
      setError(
        "Failed to update admin. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Delete admin
  const deleteAdmin = async (adminId) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;

    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/admins/${adminId}`,
        {
          withCredentials: true,
        }
      );
      fetchAdmins();
    } catch (err) {
      setError(
        "Failed to delete admin. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Delete tester
  const deleteTester = async (testerId) => {
    if (!window.confirm("Are you sure you want to delete this tester?")) return;

    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/testers/${testerId}`,
        {
          withCredentials: true,
        }
      );
      fetchTesters();
    } catch (err) {
      setError(
        "Failed to delete tester. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Save access rights
  const saveAccessRights = async () => {
    try {
      setLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/admins/${currentAdmin.id}/access`,
        { accessRights },
        { withCredentials: true }
      );
      setShowAccessModal(false);
      setLoading(false);
    } catch (err) {
      setError(
        "Failed to update access rights. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Save tester access rights
  const saveTesterAccessRights = async () => {
    try {
      setLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/super-admin/testers/${currentTester.id}/access`,
        { accessRights: testerAccessRights },
        { withCredentials: true }
      );
      setShowTesterAccessModal(false);
      setLoading(false);
    } catch (err) {
      setError(
        "Failed to update tester access rights. " +
          (err.response?.data?.message || err.message)
      );
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="font-sans max-w-6xl mx-auto p-5 bg-gray-50 min-h-screen">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center py-5 border-b border-gray-300 mb-8">
        <h1 className="text-3xl font-semibold text-gray-800 m-0">
          Super Admin Dashboard
        </h1>
        <div className="flex flex-wrap gap-3 sm:gap-4">
          <Link
            to="/DsportAdmin"
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-500 text-white border-none rounded text-sm font-medium cursor-pointer no-underline transition-colors duration-200 hover:bg-gray-600 w-full sm:w-auto justify-center"
          >
            <FaSignOutAlt /> Admin Panel
          </Link>
          <button
            className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white border-none rounded text-sm font-medium cursor-pointer no-underline transition-colors duration-200 hover:bg-red-600 w-full sm:w-auto justify-center"
            onClick={handleLogout}
          >
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-5 border border-red-200">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
        <button
          className={`flex items-center gap-2 px-6 py-3 border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 ${
            activeTab === "admins"
              ? "bg-blue-500 text-white"
              : "bg-white text-gray-600 hover:bg-gray-100"
          }`}
          onClick={() => setActiveTab("admins")}
        >
          <FaUsers /> Admin Management
        </button>
        <button
          className={`flex items-center gap-2 px-6 py-3 border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 ${
            activeTab === "testers"
              ? "bg-blue-500 text-white"
              : "bg-white text-gray-600 hover:bg-gray-100"
          }`}
          onClick={() => setActiveTab("testers")}
        >
          <FaFlask /> Tester Management
        </button>
      </div>

      {/* Admin Management Tab */}
      {activeTab === "admins" && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex justify-between items-center p-5 border-b border-gray-300 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-800 m-0 flex items-center gap-2.5">
              <FaUsers /> Admin Management
            </h2>
            <button
              className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-green-600"
              onClick={openAddModal}
            >
              <FaUserPlus /> Add New Admin
            </button>
          </div>
          {loading ? (
            <div className="text-center py-10 text-gray-500 text-lg">
              Loading...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Username
                    </th>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Email
                    </th>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Type
                    </th>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr
                      key={admin.id}
                      className="transition-colors duration-200 hover:bg-gray-50"
                    >
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        {admin.username}
                      </td>
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        {admin.email}
                      </td>
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        <span
                          className={`inline-block py-1 px-2 rounded text-xs font-medium text-center ${
                            admin.adminType === "SuperAdmin"
                              ? "bg-orange-100 text-orange-600"
                              : "bg-blue-100 text-blue-600"
                          }`}
                        >
                          {admin.adminType === "SuperAdmin"
                            ? "Super Admin"
                            : "Regular Admin"}
                        </span>
                      </td>
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        <div className="flex gap-2.5">
                          <button
                            className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white border-none rounded cursor-pointer transition-colors duration-200 hover:bg-blue-600"
                            onClick={() => openEditModal(admin)}
                            title="Edit Admin"
                          >
                            <FaEdit />
                          </button>
                          {admin.adminType !== "SuperAdmin" && (
                            <button
                              className="flex items-center justify-center w-8 h-8 bg-orange-500 text-white border-none rounded cursor-pointer transition-colors duration-200 hover:bg-orange-600"
                              onClick={() => openAccessModal(admin)}
                              title="Manage Access"
                            >
                              <FaKey />
                            </button>
                          )}
                          <button
                            className="flex items-center justify-center w-8 h-8 bg-red-500 text-white border-none rounded cursor-pointer transition-colors duration-200 hover:bg-red-600"
                            onClick={() => deleteAdmin(admin.id)}
                            title="Delete Admin"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tester Management Tab */}
      {activeTab === "testers" && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex justify-between items-center p-5 border-b border-gray-300 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-800 m-0 flex items-center gap-2.5">
              <FaFlask /> Tester Management
            </h2>
            <button
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-purple-600"
              onClick={openAddTesterModal}
            >
              <FaFlask /> Create New Tester
            </button>
          </div>
          {loading ? (
            <div className="text-center py-10 text-gray-500 text-lg">
              Loading...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse">
                <thead>
                  <tr>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Username
                    </th>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Email
                    </th>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Name
                    </th>
                    <th className="bg-gray-50 text-gray-600 font-semibold text-left py-4 px-5 border-b-2 border-gray-300 text-sm">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {testers.map((tester) => (
                    <tr
                      key={tester.id}
                      className="transition-colors duration-200 hover:bg-gray-50"
                    >
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        {tester.user_name}
                      </td>
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        {tester.email}
                      </td>
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        {tester.first_name} {tester.last_name}
                      </td>
                      <td className="py-4 px-5 border-b border-gray-300 text-gray-800 text-sm">
                        <div className="flex gap-2.5">
                          <button
                            className="flex items-center justify-center w-8 h-8 bg-orange-500 text-white border-none rounded cursor-pointer transition-colors duration-200 hover:bg-orange-600"
                            onClick={() => openTesterAccessModal(tester)}
                            title="Manage Access"
                          >
                            <FaKey />
                          </button>
                          <button
                            className="flex items-center justify-center w-8 h-8 bg-red-500 text-white border-none rounded cursor-pointer transition-colors duration-200 hover:bg-red-600"
                            onClick={() => deleteTester(tester.id)}
                            title="Delete Tester"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-lg shadow-xl overflow-hidden">
            <div className="flex justify-between items-center py-4 px-5 border-b border-gray-300 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800 m-0">
                Add New Admin
              </h2>
              <button
                className="bg-transparent border-none text-xl cursor-pointer text-gray-500 flex items-center justify-center hover:text-gray-700"
                onClick={() => setShowAddModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={addAdmin} className="p-5">
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Username:
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Email:
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Password:
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Admin Type:
                </label>
                <select
                  name="adminType"
                  value={formData.adminType}
                  onChange={handleInputChange}
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={2}>Regular Admin</option>
                  <option value={3}>Super Admin</option>
                </select>
              </div>
              <div className="flex justify-end gap-2.5 py-4 px-5 border-t border-gray-300 bg-gray-50 -mx-5 -mb-5 mt-5">
                <button
                  type="submit"
                  className="flex items-center gap-2 py-2.5 px-4 bg-green-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-green-600"
                >
                  <FaSave /> Save
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 py-2.5 px-4 bg-gray-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-gray-600"
                  onClick={() => setShowAddModal(false)}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Tester Modal */}
      {showAddTesterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-lg shadow-xl overflow-hidden">
            <div className="flex justify-between items-center py-4 px-5 border-b border-gray-300 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800 m-0">
                Create New Tester
              </h2>
              <button
                className="bg-transparent border-none text-xl cursor-pointer text-gray-500 flex items-center justify-center hover:text-gray-700"
                onClick={() => setShowAddTesterModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={addTester} className="p-5">
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Email:
                </label>
                <input
                  type="email"
                  name="email"
                  value={testerFormData.email}
                  onChange={handleTesterInputChange}
                  required
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Password:
                </label>
                <input
                  type="password"
                  name="password"
                  value={testerFormData.password}
                  onChange={handleTesterInputChange}
                  required
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {registrationLink && (
                <div className="mb-5 p-4 bg-purple-50 border border-purple-200 rounded">
                  <label className="block mb-2 font-medium text-purple-800 text-sm">
                    Registration Link (Share this with the tester):
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={registrationLink}
                      readOnly
                      className="flex-1 py-2 px-3 text-xs border border-gray-300 rounded bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className={`flex items-center gap-1 px-3 py-2 text-xs font-medium rounded transition-colors duration-200 ${
                        linkCopied
                          ? "bg-green-500 text-white"
                          : "bg-purple-500 text-white hover:bg-purple-600"
                      }`}
                    >
                      {linkCopied ? <FaCheck /> : <FaCopy />}
                      {linkCopied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    The tester can use this link to complete their registration
                    without receiving an email.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2.5 py-4 px-5 border-t border-gray-300 bg-gray-50 -mx-5 -mb-5 mt-5">
                {!registrationLink ? (
                  <button
                    type="submit"
                    className="flex items-center gap-2 py-2.5 px-4 bg-purple-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-purple-600"
                  >
                    <FaSave /> Create Tester
                  </button>
                ) : (
                  <button
                    type="button"
                    className="flex items-center gap-2 py-2.5 px-4 bg-green-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-green-600"
                    onClick={() => setShowAddTesterModal(false)}
                  >
                    <FaCheck /> Done
                  </button>
                )}
                <button
                  type="button"
                  className="flex items-center gap-2 py-2.5 px-4 bg-gray-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-gray-600"
                  onClick={() => setShowAddTesterModal(false)}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-lg shadow-xl overflow-hidden">
            <div className="flex justify-between items-center py-4 px-5 border-b border-gray-300 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800 m-0">
                Edit Admin
              </h2>
              <button
                className="bg-transparent border-none text-xl cursor-pointer text-gray-500 flex items-center justify-center hover:text-gray-700"
                onClick={() => setShowEditModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={updateAdmin} className="p-5">
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Username:
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Email:
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">
                  Password (leave blank to keep current):
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Leave blank to keep current password"
                  className="w-full py-2.5 px-3 text-sm border border-gray-300 rounded box-border focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-2.5 py-4 px-5 border-t border-gray-300 bg-gray-50 -mx-5 -mb-5 mt-5">
                <button
                  type="submit"
                  className="flex items-center gap-2 py-2.5 px-4 bg-green-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-green-600"
                >
                  <FaSave /> Save
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 py-2.5 px-4 bg-gray-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-gray-600"
                  onClick={() => setShowEditModal(false)}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Access Rights Modal */}
      {showAccessModal && currentAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-lg shadow-xl overflow-hidden">
            <div className="flex justify-between items-center py-4 px-5 border-b border-gray-300 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800 m-0">
                Manage Access Rights for {currentAdmin.username}
              </h2>
              <button
                className="bg-transparent border-none text-xl cursor-pointer text-gray-500 flex items-center justify-center hover:text-gray-700"
                onClick={() => setShowAccessModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-5">
              <p className="mb-4 text-gray-600 text-sm">
                Select modules that this admin can access:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
                {Object.keys(accessRights).map((module) => (
                  <div
                    className="p-2.5 rounded bg-gray-50 border border-gray-300"
                    key={module}
                  >
                    <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={accessRights[module]}
                        onChange={() => handleAccessToggle(module)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      {moduleDisplayNames[module]}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2.5 py-4 px-5 border-t border-gray-300 bg-gray-50">
              <button
                onClick={saveAccessRights}
                className="flex items-center gap-2 py-2.5 px-4 bg-green-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-green-600"
              >
                <FaSave /> Save Access Rights
              </button>
              <button
                onClick={() => setShowAccessModal(false)}
                className="flex items-center gap-2 py-2.5 px-4 bg-gray-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-gray-600"
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tester Access Rights Modal */}
      {showTesterAccessModal && currentTester && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-lg shadow-xl overflow-hidden">
            <div className="flex justify-between items-center py-4 px-5 border-b border-gray-300 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800 m-0">
                Manage Access Rights for {currentTester.user_name}
              </h2>
              <button
                className="bg-transparent border-none text-xl cursor-pointer text-gray-500 flex items-center justify-center hover:text-gray-700"
                onClick={() => setShowTesterAccessModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-5">
              <p className="mb-4 text-gray-600 text-sm">
                Select modules that this tester can access:
              </p>
              <div className="grid grid-cols-1 gap-2.5 mb-5">
                {Object.keys(testerAccessRights).map((module) => (
                  <div
                    className="p-2.5 rounded bg-purple-50 border border-purple-200"
                    key={module}
                  >
                    <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={testerAccessRights[module]}
                        onChange={() => handleTesterAccessToggle(module)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      {testerModuleDisplayNames[module]}
                    </label>
                  </div>
                ))}
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                <strong>Note:</strong> Testers have limited access compared to
                regular admins. They can only access specific testing modules.
              </div>
            </div>
            <div className="flex justify-end gap-2.5 py-4 px-5 border-t border-gray-300 bg-gray-50">
              <button
                onClick={saveTesterAccessRights}
                className="flex items-center gap-2 py-2.5 px-4 bg-purple-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-purple-600"
              >
                <FaSave /> Save Access Rights
              </button>
              <button
                onClick={() => setShowTesterAccessModal(false)}
                className="flex items-center gap-2 py-2.5 px-4 bg-gray-500 text-white border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-gray-600"
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DsportSuperAdminDashboard;
